import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Gift, Plus, Pencil, Trash2, Ticket, Check, Coins, ScanLine, X, Archive, ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { FindoSpinner } from "@/components/FindoSpinner";

const empty = { title: "", description: "", points_cost: 5, stock: "", validity_days: 60, active: true };
const SCAN_REGION = "voucher-scan-region";

export default function PartnerRewardsManager() {
  const { t } = useLang();
  const { partner } = useAuth();
  const canManage = partner?.role === "owner" || partner?.role === "admin";
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} (new) | reward
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => {
    api("/partners/me/rewards").then((r) => setRewards(r.rewards || [])).catch(() => {});
    api("/partners/me/rewards-redemptions").then((r) => setRedemptions(r.redemptions || [])).catch(() => {});
    api("/partners/me/rewards-stats").then(setStats).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  function openNew() { setForm(empty); setEditing({}); }
  function openEdit(r) {
    setForm({ title: r.title, description: r.description || "", points_cost: r.points_cost, stock: r.stock ?? "", validity_days: r.validity_days ?? 60, active: r.active });
    setEditing(r);
  }

  async function save() {
    setBusy(true);
    try {
      const body = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        points_cost: Number(form.points_cost) || 1,
        stock: form.stock === "" ? null : Number(form.stock),
        validity_days: Number(form.validity_days) || 60,
        active: !!form.active,
      };
      if (editing && editing.id) await api(`/partners/me/rewards/${editing.id}`, { method: "PATCH", body });
      else await api("/partners/me/rewards", { method: "POST", body });
      toast.success(t("rw.saved"));
      setEditing(null);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  async function remove(r) {
    if (!window.confirm(t("rw.confirmDelete").replace("{title}", r.title))) return;
    try { await api(`/partners/me/rewards/${r.id}`, { method: "DELETE" }); toast.success(t("rw.deleted")); load(); }
    catch (e) { toast.error(e.message); }
  }

  async function markUsed(code) {
    try { await api(`/partners/me/rewards-redemptions/${code}/use`, { method: "POST" }); toast.success(t("rw.codeValidated")); load(); window.dispatchEvent(new Event("findloop:rewards-changed")); return true; }
    catch (e) { toast.error(e.message); return false; }
  }

  async function onScan(code) {
    setScanOpen(false);
    const clean = (code || "").trim().toUpperCase();
    if (!clean) return;
    await markUsed(clean);
  }

  return (
    <div className="bg-white rounded-2xl border border-border p-6 card-shadow mt-6" data-testid="rewards-manager-card">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Gift size={18} className="text-findo-indigo" />
          <h3 className="font-display font-bold text-slate-900">{t("rw.title")}</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setScanOpen(true)} className="rounded-full font-semibold" data-testid="scan-voucher-btn">
            <ScanLine size={15} className="mr-1.5" /> {t("rw.scanVoucher")}
          </Button>
          {canManage && (
            <Button size="sm" onClick={openNew} className="rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="reward-add-btn">
              <Plus size={15} className="mr-1.5" /> {t("rw.add")}
            </Button>
          )}
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-4 max-w-2xl">
        {t("rw.subtitle")}
      </p>

      {/* Mini dashboard */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="rewards-stats">
          <StatCard label={t("rw.statRedeemed")} value={stats.redeemed} testid="stat-redeemed" />
          <StatCard label={t("rw.statUsed")} value={stats.used} tone="emerald" testid="stat-used" />
          <StatCard label={t("rw.statActive")} value={stats.active} tone="navy" testid="stat-active" />
          <StatCard label={t("rw.statExpired")} value={stats.expired} tone="red" testid="stat-expired" />
        </div>
      )}

      {rewards.length === 0 ? (
        <div className="text-center text-slate-400 py-10 border border-dashed border-border rounded-xl" data-testid="rewards-empty">
          <Gift size={32} className="mx-auto text-slate-200" />
          <p className="mt-2 text-sm">{t("rw.empty")}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="rewards-list">
          {rewards.map((r) => (
            <div key={r.id} className="rounded-xl border border-border p-4 flex flex-col" data-testid={`reward-item-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold text-slate-800">{r.title}</p>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{r.active ? t("rw.active") : t("rw.inactive")}</span>
              </div>
              {r.description && <p className="text-xs text-slate-500 mt-1 flex-1">{r.description}</p>}
              <div className="flex items-center gap-3 text-xs text-slate-500 mt-2 flex-wrap">
                <span className="inline-flex items-center gap-1 font-bold text-findo-indigo"><Coins size={13} /> {r.points_cost} pts</span>
                <span>{t("rw.stock")} : {r.stock === null || r.stock === undefined ? "∞" : r.stock}</span>
                <span>{t("rw.validity").replace("{n}", r.validity_days ?? 60)}</span>
                <span>{t("rw.redeemedN").replace("{n}", r.redeemed_count)}</span>
              </div>
              <div className="flex gap-2 mt-3">
                {canManage ? (
                  <>
                    <Button size="sm" variant="outline" onClick={() => openEdit(r)} className="rounded-full h-8" data-testid={`reward-edit-${r.id}`}><Pencil size={13} className="mr-1" /> {t("rw.edit")}</Button>
                    <Button size="sm" variant="outline" onClick={() => remove(r)} className="rounded-full h-8 text-findo-danger border-findo-danger/30" data-testid={`reward-delete-${r.id}`}><Trash2 size={13} /></Button>
                  </>
                ) : (
                  <span className="text-[11px] text-slate-400 italic">{t("rw.staffScanOnly")}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Redemptions */}
      <div className="mt-6">
        <h4 className="font-semibold text-slate-800 text-sm flex items-center gap-1.5 mb-2"><Ticket size={15} /> {t("rw.activeCodes")}</h4>
        {(() => {
          const active = redemptions.filter((rd) => rd.status !== "used");
          const archived = redemptions.filter((rd) => rd.status === "used");
          return (
          <>
            {active.length === 0 ? (
              <p className="text-xs text-slate-400">{t("rw.noExchange")}</p>
            ) : (
              <div className="divide-y divide-border border border-border rounded-xl" data-testid="redemptions-list">
                {active.map((rd) => (
                  <div key={rd.id} className="flex items-center justify-between gap-3 p-3 flex-wrap" data-testid={`redemption-${rd.code}`}>
                    <div className="min-w-0">
                      <p className="font-mono font-bold text-findo-navy text-sm">{rd.code}</p>
                      <p className="text-xs text-slate-400">{rd.reward_title} · {rd.points_spent} pts · {rd.user_email}</p>
                      {rd.expires_at && <p className="text-[11px] text-slate-400">{t("rw.expiresOn").replace("{date}", new Date(rd.expires_at).toLocaleDateString())}</p>}
                    </div>
                    {rd.expired ? (
                      <span className="text-xs font-bold text-findo-danger inline-flex items-center gap-1" data-testid={`redemption-expired-${rd.code}`}>{t("rw.expired")}</span>
                    ) : (
                      <Button size="sm" onClick={() => markUsed(rd.code)} className="rounded-full h-8 bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid={`redemption-use-${rd.code}`}>{t("rw.validateCode")}</Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {archived.length > 0 && (
              <div className="mt-4" data-testid="archived-redemptions">
                <button
                  type="button"
                  onClick={() => setShowArchived((v) => !v)}
                  className="w-full flex items-center justify-between gap-2 rounded-xl border border-border bg-slate-50 hover:bg-slate-100 px-4 py-2.5 transition-colors"
                  data-testid="toggle-archived-redemptions"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Archive size={15} /> {t("rw.archivedTitle")} <span className="text-xs font-normal text-slate-400">({archived.length})</span></span>
                  <ChevronDown size={18} className={`text-slate-400 transition-transform ${showArchived ? "rotate-180" : ""}`} />
                </button>
                {showArchived && (
                  <div className="mt-2 divide-y divide-border border border-border rounded-xl" data-testid="archived-redemptions-list">
                    {archived.map((rd) => (
                      <div key={rd.id} className="flex items-center justify-between gap-3 p-3 flex-wrap opacity-80" data-testid={`archived-redemption-${rd.code}`}>
                        <div className="min-w-0">
                          <p className="font-mono font-bold text-slate-500 text-sm">{rd.code}</p>
                          <p className="text-xs text-slate-400">{rd.reward_title} · {rd.points_spent} pts · {rd.user_email}</p>
                          {rd.used_at && <p className="text-[11px] text-slate-400">{new Date(rd.used_at).toLocaleDateString()}</p>}
                        </div>
                        <span className="text-xs font-bold text-slate-400 inline-flex items-center gap-1"><Archive size={12} /> {t("rw.archived")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
          );
        })()}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="reward-dialog">
          <DialogHeader><DialogTitle>{editing && editing.id ? t("rw.editReward") : t("rw.newReward")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("rw.fTitle")}</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="reward-form-title" /></div>
            <div><Label>{t("rw.fDesc")}</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 rounded-xl" data-testid="reward-form-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("rw.fCost")}</Label><Input type="number" min="1" value={form.points_cost} onChange={(e) => setForm({ ...form, points_cost: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="reward-form-cost" /></div>
              <div><Label>{t("rw.fStock")}</Label><Input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="reward-form-stock" /></div>
            </div>
            <div><Label>{t("rw.fValidity")}</Label><Input type="number" min="1" max="730" value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="reward-form-validity" /></div>
            <label className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
              <span className="text-sm font-semibold text-slate-700">{t("rw.fActive")}</span>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="reward-form-active" />
            </label>
            <Button onClick={save} disabled={busy || !form.title.trim()} className="w-full rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="reward-form-save">{busy ? <FindoSpinner size={16} /> : t("c.save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <VoucherScanDialog open={scanOpen} onClose={() => setScanOpen(false)} onResult={onScan} onManual={markUsed} />
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const toneCls = { emerald: "text-emerald-600", navy: "text-findo-navy", red: "text-findo-red" }[tone] || "text-slate-800";
  return (
    <div className="rounded-xl border border-border bg-findo-cream/40 p-3 text-center" data-testid={testid}>
      <p className={`font-display text-2xl font-semibold ${toneCls}`}>{value ?? 0}</p>
      <p className="text-[11px] font-semibold text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

function VoucherScanDialog({ open, onClose, onResult, onManual }) {
  const { t } = useLang();
  const scannerRef = useRef(null);
  const resultRef = useRef(onResult);
  resultRef.current = onResult;
  const [camState, setCamState] = useState("starting");
  const [manual, setManual] = useState("");

  // Only depends on `open` so the camera initialises exactly once per opening
  // (avoids the start/stop thrash that caused "Cannot stop, scanner is not running").
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let scanner = null;
    setCamState("starting");
    setManual("");

    // Stop only when the scanner is actually running; guard every call so a
    // cancel/close can never throw an uncaught error (red screen).
    const stopSafely = async () => {
      const s = scanner;
      scanner = null;
      scannerRef.current = null;
      if (!s) return;
      try {
        const st = typeof s.getState === "function" ? s.getState() : null;
        if (st === Html5QrcodeScannerState.SCANNING || st === Html5QrcodeScannerState.PAUSED) {
          await s.stop();
        }
      } catch (e) { /* scanner already stopped — ignore */ }
      try { s.clear(); } catch (e) { /* ignore */ }
    };

    // Defer one tick so the dialog's portal content (the scan region div) is
    // mounted before html5-qrcode looks it up — otherwise it throws synchronously.
    const timer = setTimeout(() => {
      if (cancelled) return;
      try {
        scanner = new Html5Qrcode(SCAN_REGION, { verbose: false });
        scannerRef.current = scanner;
        scanner
          .start(
            { facingMode: "environment" },
            { fps: 10, qrbox: { width: 220, height: 220 } },
            (decoded) => {
              if (cancelled) return;
              cancelled = true;
              stopSafely().finally(() => resultRef.current?.(decoded));
            },
            () => {},
          )
          .then(() => { if (!cancelled) setCamState("running"); })
          .catch(() => { if (!cancelled) setCamState("error"); });
      } catch (e) {
        setCamState("error");
      }
    }, 60);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      stopSafely();
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="voucher-scan-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><ScanLine size={20} className="text-findo-indigo" /> {t("rw.scanVoucher")}</DialogTitle></DialogHeader>
        <div className="rounded-xl overflow-hidden bg-slate-900 relative" style={{ minHeight: 220 }}>
          <div id={SCAN_REGION} className="w-full" />
          {camState === "error" && <p className="absolute inset-0 grid place-items-center text-white/80 text-sm p-6 text-center">{t("rw.camUnavailable")}</p>}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Input value={manual} onChange={(e) => setManual(e.target.value.toUpperCase())} placeholder="FND-XXXXXXXX" className="h-11 rounded-xl font-mono" data-testid="voucher-manual-input" />
          <Button onClick={async () => { const ok = await onManual(manual.trim()); if (ok) onClose(); }} disabled={!manual.trim()} className="rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="voucher-manual-validate">{t("rw.validate")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
